import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Shield, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/Button';
import { WizardStepHeader } from '../../components/wizard';
import {
  useEmergencyFundConfigQuery,
  useSimulatePlanMutation,
  useToggleSimulationFavoriteMutation,
} from '../../features/emergencyFund/queries';
import {
  composeFundSplit,
  groupPlanBenefits,
  parseAmount,
} from '../../lib/emergencyFundDisplayUtils';
import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

const BENEFIT_ICONS = {
  immediate: Lightning,
  yield: Leaf,
  safe: Shield,
};

function BenefitCard({ id, amountEur, copy, emphasized }) {
  const Icon = BENEFIT_ICONS[id] || Shield;
  const benefit = copy.benefits[id];
  if (!benefit) return null;

  return (
    <div
      className={cn(
        'flex gap-3 items-start rounded-2xl border px-[15px] py-3.5',
        emphasized
          ? 'border-sage-600 bg-[#F4F7F0] dark:border-sage-600 dark:bg-sage-900/40'
          : 'border-border bg-card',
      )}
    >
      <span
        className={cn(
          'flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl',
          emphasized
            ? 'bg-sage-600 text-white'
            : 'bg-muted text-muted-foreground',
        )}
        aria-hidden
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-display text-[14.5px] font-semibold text-foreground">
            {benefit.title}
          </span>
          <span className="font-display text-sm font-bold tabular-nums text-sage-800 dark:text-sage-300">
            {formatEUR(amountEur)}
          </span>
        </div>
        <p className="mt-1.5 mb-0 text-xs leading-snug text-muted-foreground">
          {benefit.body}
        </p>
      </div>
    </div>
  );
}

export function EmergencyFundPlanStep() {
  const copy = pt.emergencyFund.wizard.plan;
  const { data: config } = useEmergencyFundConfigQuery();
  const simulate = useSimulatePlanMutation();
  const toggleFavorite = useToggleSimulationFavoriteMutation();
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await simulate.mutateAsync('balanced');
        if (!cancelled) setLatest(result);
      } catch (err) {
        if (!cancelled) {
          toast.error(typeof err === 'string' ? err : copy.simulateError);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const months = Number(config?.ef_months_of_coverage ?? latest?.months_coverage ?? 3);
  const targetFromConfig = parseAmount(config?.ef_custom_target);
  const totalEur =
    parseAmount(latest?.total_target_eur) || targetFromConfig;
  const avgMonthly =
    months > 0 && totalEur > 0 ? totalEur / months : 0;
  const split = composeFundSplit(avgMonthly, months);

  const benefitCards = useMemo(
    () =>
      groupPlanBenefits(latest?.tiers ?? [], {
        immediateEur: split.immediateEur,
        totalEur,
      }),
    [latest?.tiers, split.immediateEur, totalEur],
  );

  const handleApply = async () => {
    if (!latest?.id) {
      toast.success(copy.appliedToast);
      return;
    }
    try {
      await toggleFavorite.mutateAsync(latest.id);
      toast.success(copy.appliedToast);
    } catch {
      toast.success(copy.appliedToast);
    }
  };

  const loading = simulate.isPending && !latest;

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
          {copy.badgeOptional}
        </span>
        <span className="text-[11px] font-semibold text-muted-foreground">
          {copy.badgeAdvanced}
        </span>
      </div>

      <WizardStepHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
        {copy.disclaimer}
      </p>

      {loading ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <p className="text-sm text-muted-foreground">{copy.loadingHint}</p>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[76px] animate-pulse rounded-2xl bg-muted"
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-[11px]">
          {benefitCards.map((card, index) => (
            <BenefitCard
              key={card.id}
              id={card.id}
              amountEur={card.amountEur}
              copy={copy}
              emphasized={index < 2}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1.5 border-t border-border pt-3">
        <Button
          type="button"
          className="h-12 w-full rounded-[14px] text-[15px] font-semibold"
          onClick={handleApply}
          loading={toggleFavorite.isPending}
          disabled={loading}
        >
          {copy.applyCta}
        </Button>
        <Button variant="ghost" asChild className="h-11 font-semibold text-muted-foreground">
          <Link to={paths.emergencyFund}>{copy.skipCta}</Link>
        </Button>
      </div>
    </div>
  );
}

export default EmergencyFundPlanStep;
